import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  OVERLAP_STATUS,
  OVERLAP_STATUS_OPTIONS,
} from "common/constants/overlap-status";
import { extractFieldErrors } from "common/lib/fieldErrors";
import { resolveOverlappingPayments } from "common/services/payments.service";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Col,
  Form,
  FormFeedback,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import MergePaymentsModal from "./MergePaymentsModal";
import OverlapStatusCards from "./OverlapStatusCards";
import PaymentComparison from "./PaymentComparison";
import {
  DoubleEntryMergeNotice,
  describeResolveOutcomes,
  resolveErrorMessage,
} from "./resolution";

const ResolvePayment: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchData: () => void;
  selectedRecord: any;
  /**
   * Called once a double entry is merged. The overlap record no longer exists
   * afterwards, so a page showing just that record should leave; defaults to
   * refetching.
   */
  onMerged?: () => void;
}> = ({ toggle, isModalOpen, fetchData, selectedRecord, onMerged }) => {
  const [loading, setLoading] = useState<boolean>(false);
  // Double entry swaps this form for the merge form rather than resolving.
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!isModalOpen) setMerging(false);
  }, [isModalOpen]);
  const [, setFieldErrors] = useState<any>([]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: "",
      overlapStatus: "",
    },
    validationSchema: Yup.object({
      comments: Yup.string(),
      overlapStatus: Yup.string()
        .oneOf(
          OVERLAP_STATUS_OPTIONS.map((option) => option.value),
          "Please select a valid status"
        )
        .required("Please select a status"),
    }),
    onSubmit: async (values: any) => {
      if (values.overlapStatus === OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY) {
        setMerging(true);
        return;
      }

      setLoading(true);
      try {
        const response: any = await resolveOverlappingPayments({
          ids: [selectedRecord?.id],
          overlapStatus: values.overlapStatus,
          comments: values.comments,
        });
        const outcome = response?.outcomes?.[0];
        if (!outcome || outcome.status === "failed") {
          toast.error(outcome?.error || "Payment resolution failed");
          return;
        }
        fetchData();
        validation.resetForm();
        toggle();
        toast.success(describeResolveOutcomes(response.outcomes).message);
      } catch (e: any) {
        const errors = extractFieldErrors(e?.response);
        setFieldErrors(errors);
        toast.error(resolveErrorMessage(e, "Payment resolution failed"));
      } finally {
        setLoading(false);
      }
    },
  });

  // Update fieldErrors when validation errors change
  useEffect(() => {
    const validationErrors = [];

    // Convert Formik validation errors to fieldErrors format
    for (const [fieldName, errorMessage] of Object.entries(validation.errors)) {
      if (validation.touched[fieldName] && errorMessage) {
        // Handle nested object errors (like individual.value, mda.value, etc.)
        if (typeof errorMessage === "object" && errorMessage !== null) {
          for (const [nestedField, nestedError] of Object.entries(
            errorMessage
          )) {
            if (nestedError) {
              validationErrors.push({
                field: `${fieldName}.${nestedField}`,
                message: nestedError,
              });
            }
          }
        } else {
          validationErrors.push({
            field: fieldName,
            message: errorMessage,
          });
        }
      }
    }

    setFieldErrors(validationErrors);
  }, [validation.errors, validation.touched]);

  console.log(validation.errors);
  //   console.log(validation.values);

  const isDoubleEntry =
    validation.values.overlapStatus ===
    OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY;

  return (
    <>
      <MergePaymentsModal
        isOpen={isModalOpen && merging}
        recordId={selectedRecord?.id}
        comments={validation.values.comments}
        onBack={() => setMerging(false)}
        onCancel={toggle}
        onMerged={() => {
          validation.resetForm();
          toggle();
          if (onMerged) onMerged();
          else fetchData();
        }}
      />
      <Modal
        id="showModal"
        size="xl"
        isOpen={isModalOpen && !merging}
        toggle={toggle}
        centered
      >
        <ModalHeader className="bg-light p-3" toggle={toggle}>
          Resolve Duplicate Payment
        </ModalHeader>

        <Form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <PaymentComparison record={selectedRecord} />
            <Row>
              <Col md={12} className="mb-4">
                <div id="overlapStatus-label" className="form-label">
                  What is this payment?
                </div>
                <OverlapStatusCards
                  name="overlapStatus"
                  idPrefix="resolveOverlapStatus"
                  labelledBy="overlapStatus-label"
                  value={validation.values.overlapStatus}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.overlapStatus &&
                    !!validation.errors.overlapStatus
                  }
                />
                {validation.touched.overlapStatus &&
                validation.errors.overlapStatus ? (
                  // FormFeedback only shows next to an invalid <input>, which
                  // the cards are not, so force it visible.
                  <div className="invalid-feedback d-block">
                    {/* @ts-ignore */}
                    {validation.errors.overlapStatus}
                  </div>
                ) : null}
                <DoubleEntryMergeNotice
                  status={validation.values.overlapStatus}
                />
              </Col>
              <Col md={12} className="mb-3">
                <Label htmlFor="id-field" className="form-label">
                  Comments on resolution (Optional)
                </Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={validation.values.comments || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    validation.setFieldValue("comments", data);
                  }}
                  onBlur={() => {
                    validation.handleBlur("comments");
                  }}
                />
                {validation.touched.comments && validation.errors.comments ? (
                  <FormFeedback type="invalid">
                    {/* @ts-ignore */}
                    {validation.errors?.comments}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>
          </ModalBody>

          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  toggle();
                }}
              >
                Close
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading && (
                  <Spinner
                    color="light"
                    size="sm"
                    style={{ marginRight: "8px", marginBottom: "-1px" }}
                  ></Spinner>
                )}
                {isDoubleEntry ? "Continue to Merge" : "Resolve Payment"}
              </button>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ResolvePayment;
